/**
 * =============================================================================
 * Componente: InfiniteScrollSentinel
 * Versão: 1.0.0
 *
 * Descrição:
 *   Sentinela leve para scroll infinito baseada em IntersectionObserver nativo.
 *   Não polla, não escuta scroll global. Quando o elemento entra no viewport
 *   (com `rootMargin` opcional), dispara `onLoadMore()` — mas só se houver
 *   mais páginas e nenhuma busca em andamento.
 *
 * Uso típico:
 *   <InfiniteScrollSentinel
 *     hasMore={hasNextPage}
 *     isLoading={isFetchingNextPage}
 *     onLoadMore={fetchNextPage}
 *   />
 *
 * Release notes:
 *   v1.0.0 - Implementação inicial.
 * =============================================================================
 */
import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InfiniteScrollSentinelProps {
  /** Existe próxima página? */
  hasMore: boolean;
  /** Está buscando a próxima página? */
  isLoading: boolean;
  /** Callback para solicitar mais itens. */
  onLoadMore: () => void;
  /** Margem para antecipar a busca (default 200px). */
  rootMargin?: string;
  /** Classe extra para o container. */
  className?: string;
}

export function InfiniteScrollSentinel({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '200px',
  className,
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Sem mais páginas: nada a observar
    if (!hasMore) return;
    const node = ref.current;
    if (!node) return;

    // IntersectionObserver nativo — barato, sem listeners de scroll
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore, rootMargin]);

  if (!hasMore && !isLoading) return null;

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center py-4 text-xs text-muted-foreground',
        className,
      )}
    >
      {isLoading ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Carregando mais...
        </span>
      ) : (
        <span className="opacity-60">Role para carregar mais</span>
      )}
    </div>
  );
}
